import { getToken } from "./auth/session.js";
import { doRefresh } from "./auth/client.js";

// Same-origin in production (reverse proxy) AND in dev (Vite /api proxy —
// see vite.config.js), so no CORS and no baked-in backend host.
const API_BASE = "/api";

// Requests are authenticated with the logged-in user's dashboard-api session
// token — the store-analytics backend validates it upstream. On a 401 we
// refresh the session once and retry; if that fails we return the error body
// as before so data callers can fall back to mock data without logging the
// user out.
async function apiGet(path, params, _isRetry = false) {
  const qs = params instanceof URLSearchParams ? params.toString() : "";
  const url = `${API_BASE}${path}${qs ? `?${qs}` : ""}`;
  const token = getToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401 && !_isRetry) {
    try {
      await doRefresh();
    } catch {
      return res.json();
    }
    return apiGet(path, params, true);
  }
  return res.json();
}

// Media (<img>/<video> src) can't send an Authorization header, so backend
// media routes accept the session token as a query param instead.
export function mediaUrl(url) {
  if (!url) return null;
  if (!url.startsWith("/api")) return url;
  const token = getToken();
  if (!token) return url;
  return `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

export async function fetchStores(status) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  return apiGet("/stores", params);
}

export async function fetchStoreSummaries(storeIds, dates, startDate, endDate) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (dates) params.set("dates", dates);
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  return apiGet("/store-summaries", params);
}

export async function fetchFootfall(storeIds, dates) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (Array.isArray(dates)) params.set("dates", dates.join(","));
  else if (dates) params.set("dates", dates);
  return apiGet("/footfall", params);
}

export async function fetchFootfallDaily(storeIds, fromDate, toDate) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (fromDate) params.set("from", fromDate);
  if (toDate) params.set("to", toDate);
  return apiGet("/footfall-daily", params);
}

export async function fetchFootfallDemographic(storeIds, fromDate, toDate) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (fromDate) params.set("from", fromDate);
  if (toDate) params.set("to", toDate);
  return apiGet("/footfall-demographic", params);
}

export async function fetchFootfallDirectoryHours(storeId, date) {
  const params = new URLSearchParams();
  if (storeId) params.set("store_id", storeId);
  if (date) params.set("date", date);
  return apiGet("/footfall-directory/hours", params);
}

export async function fetchFootfallDirectory(storeId, date, folder) {
  const params = new URLSearchParams();
  if (storeId) params.set("store_id", storeId);
  if (date) params.set("date", date);
  if (folder) params.set("folder", folder);
  return apiGet("/footfall-directory", params);
}

export async function fetchTrafficDensity(storeIds, date, dateRange = 7) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (date) params.set("date", date);
  params.set("dateRange", dateRange);
  return apiGet("/traffic-density", params);
}

export async function fetchDensityDwell(storeIds, fromDate, toDate, dateRange = 7) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (fromDate) params.set("from_date", fromDate);
  if (toDate) params.set("to_date", toDate);
  params.set("date_range", dateRange);
  return apiGet("/density-dwell", params);
}

export async function fetchTrafficDensityDwell(storeIds, fromDate, toDate, dateRange = 7) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (fromDate) params.set("from_date", fromDate);
  if (toDate) params.set("to_date", toDate);
  params.set("date_range", dateRange);
  return apiGet("/traffic-density-dwell", params);
}

export async function fetchSOP(storeIds, startDate, endDate, page = 1, pageSize = 10) {
  const params = new URLSearchParams();
  if (Array.isArray(storeIds)) params.set("store_ids", storeIds.join(","));
  else if (storeIds) params.set("store_ids", storeIds);
  if (startDate) params.set("start_date", startDate);
  if (endDate) params.set("end_date", endDate);
  params.set("page", page);
  params.set("page_size", pageSize);
  return apiGet("/sop", params);
}

export async function fetchTableOccupancy(storeId, date) {
  const params = new URLSearchParams();
  if (storeId) params.set("store_id", storeId);
  if (date) params.set("date", date);
  return apiGet("/table-occupancy", params);
}
